import type { ReactNode } from "react";
import type { ColumnsType } from "antd/es/table";

import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { MilestoneRowActions } from "@/components/PlanOfRecord/MilestoneRowActions";
import { ScopeBadge } from "@/components/PlanOfRecord/ScopeBadge";
import { StatusBadge } from "@/components/PlanOfRecord/StatusBadge";
import { ProvenanceBadge } from "@/components/PlanOfRecord/ProvenanceBadge";
import { getColumnTitle } from "@/utils/tableHelpers";
import { MILESTONE_TYPES, type MilestoneStatus } from "./planOfRecordData";
import { mapMilestoneRowToMilestone } from "./planOfRecordMapping";
import type {
  PlanOfRecordMilestone,
  PlanOfRecordMilestoneRow,
} from "./planOfRecord.types";

const MILESTONE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  MILESTONE_TYPES.map((entry) => [entry.value, entry.label]),
);

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    dateStyle: "medium",
  });
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
}

const FIELD_TITLES: Record<string, string> = {
  label: "Milestone",
  scope: "Scope",
  scope_label: "Scope detail",
  country_name: "Country",
  site_number: "Site",
  milestone_type: "Type",
  created_at: "Created On",
  created_by_name: "Created By",
  planned_date: "Planned",
  original_planned_date: "Originally Planned",
  drift: "Drift",
  actual_date: "Actual",
  at_risk_within_days: "At-Risk Threshold",
  last_modified_at: "Last Updated",
  last_modified_by_name: "Updated By",
  status: "Status",
  provenance: "Provenance",
  rationale: "Rationale",
  source_plan_label: "Source Plan",
};

function getFieldTitle(field: string): string {
  return FIELD_TITLES[field] || getColumnTitle(field);
}

function getFieldWidth(field: string): number {
  const fixed: Record<string, number> = {
    label: 260,
    scope: 100,
    drift: 100,
    status: 130,
    rationale: 260,
  };
  return fixed[field] ?? Math.max(100, 15 * (getFieldTitle(field).length + 1));
}

function getFieldRenderer(
  field: string,
): (text: any, record: PlanOfRecordMilestoneRow) => ReactNode {
  const renderMap: Record<
    string,
    (text: any, record: PlanOfRecordMilestoneRow) => ReactNode
  > = {
    label: (label: string, record) => (
      <div>
        <span className="font-medium text-[#1F2933]">{label}</span>
        <div className="text-[12px] text-[#8AA0A9]">{record.scope_label}</div>
      </div>
    ),
    scope: (_, record) => <ScopeBadge scope={record.scope} />,
    milestone_type: (text: string) => MILESTONE_TYPE_LABELS[text] ?? text,
    created_at: (value: string) => (
      <span className="text-[#374151]">{formatTimestamp(value)}</span>
    ),
    created_by_name: (value: string) => (
      <AvatarWithInitials
        name={value}
        avatarSize={24}
        avatarFontSize={11}
        labelFontSize={13}
      />
    ),
    planned_date: (value: string) => (
      <span className="font-medium text-[#1F2933]">{formatDate(value)}</span>
    ),
    original_planned_date: (value: string | null) => (
      <span className="text-[#374151]">{formatDate(value)}</span>
    ),
    actual_date: (value: string | null) => (
      <span className="text-[#374151]">{formatDate(value)}</span>
    ),
    at_risk_within_days: (value: number | null) =>
      value == null ? (
        <span className="text-[#8AA0A9]">—</span>
      ) : (
        `${value} days`
      ),
    drift: (_, record) => {
      const drift = record.drift;
      if (drift === null || drift === undefined) {
        return (
          <span className="text-[#8AA0A9]" title="Not yet happened.">
            —
          </span>
        );
      }

      const reference = `Planned ${formatDate(record.planned_date)}, actual ${formatDate(record.actual_date)}`;

      if (drift === 0) {
        return (
          <span className="text-[#6B7280]" title={reference}>
            On time
          </span>
        );
      }

      return (
        <span
          className={drift > 0 ? "text-[#B91C1C]" : "text-[#15803D]"}
          title={reference}
        >
          {`${drift > 0 ? "+" : ""}${drift}d`}
        </span>
      );
    },
    last_modified_at: (value: string) => (
      <span className="text-[#374151]">{formatTimestamp(value)}</span>
    ),
    last_modified_by_name: (value: string) => (
      <AvatarWithInitials
        name={value}
        avatarSize={24}
        avatarFontSize={11}
        labelFontSize={13}
      />
    ),
    status: (text: MilestoneStatus, record) => {
      const rule = record.actual_date
        ? "Actual date recorded"
        : record.at_risk_within_days != null
          ? `Reads at risk within ${record.at_risk_within_days} days of the planned date`
          : "Reads late once the planned date passes; no at-risk threshold set";
      return (
        <span title={rule}>
          <StatusBadge status={text} />
        </span>
      );
    },
    provenance: (text) => <ProvenanceBadge provenance={text} />,
  };

  const defaultRenderer = (text: any) =>
    text === null || text === undefined || text === "" ? (
      <span className="text-[#8AA0A9]">—</span>
    ) : (
      text
    );

  return renderMap[field] || defaultRenderer;
}

const omittedFields = ["id"];

interface RowActions {
  canEdit: boolean;
  onEdit: (milestone: PlanOfRecordMilestone) => void;
  onDelete: (milestone: PlanOfRecordMilestone) => void;
}

export function createMilestoneColumns(
  fields: string[],
  lockedColumns: number,
  actions?: RowActions,
  columnsTitleCreator?: (column: string) => string,
): ColumnsType<PlanOfRecordMilestoneRow> {
  const dataColumns = fields
    .filter((field) => !omittedFields.includes(field))
    .map((field, index) => ({
      title: columnsTitleCreator
        ? columnsTitleCreator(field)
        : getFieldTitle(field),
      dataIndex: field,
      key: field,
      width: getFieldWidth(field),
      fixed: index < lockedColumns ? ("left" as const) : undefined,
      render: (text: any, record: PlanOfRecordMilestoneRow) =>
        getFieldRenderer(field)(text, record),
    }));

  if (!actions?.canEdit) return dataColumns;

  return [
    ...dataColumns,
    {
      title: "",
      key: "rowActions",
      width: 88,
      fixed: "right" as const,
      render: (_: unknown, record: PlanOfRecordMilestoneRow) => (
        <MilestoneRowActions
          milestone={mapMilestoneRowToMilestone(record)}
          onEdit={actions.onEdit}
          onDelete={actions.onDelete}
        />
      ),
    },
  ];
}
